import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { message } from 'antd'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '../../hooks'
import { useCampaignPhoneNumberCountStore } from '@forwardflow/store/campaignPhoneNumberCountStore'
import CampaignService from '@forwardflow/services/CampaignService'
import {
   PhoneNumbers,
   PhoneNumberSearchInput,
   BuyNewNumbersView,
   AllocateUnusedNumbersView,
   DeletePhoneNumberModal,
   filterPhoneNumberGroups,
   formatPhoneNumber,
   usePhoneNumbers,
} from '@forwardflow/features/phone-numbers'
import type {
   IPhoneNumber,
   IAvailablePhoneNumber,
} from '@forwardflow/features/phone-numbers'
import { ChevronRightIcon } from '@forwardflow/icons'
import { motion } from 'framer-motion'
import { groupCampaignNumbers } from './utils'

type SelectedTab = 'list' | 'buy' | 'allocate'

export const BuyPhoneNumber = () => {
   const { title } = useParams()
   const { mutate: revalidateLeadSource } = useGetActiveLeadSource(
      title as string
   )
   const { data: LeadSource, mutate } = useGetActiveLeadSourceDetails(
      title as string
   )
   const { setCount } = useCampaignPhoneNumberCountStore()
   const { groupedInactivePhoneNumbers } = usePhoneNumbers()

   const [selectedTab, setSelectedTab] = useState<SelectedTab>('list')
   const [searchQuery, setSearchQuery] = useState('')
   const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
   const [numberToDelete, setNumberToDelete] = useState<IPhoneNumber | null>(
      null
   )
   const [isDeleting, setIsDeleting] = useState(false)

   const assignedNumbers = LeadSource?.assignedPhoneNumbers || []

   const groupedNumbers = useMemo(
      () => groupCampaignNumbers(assignedNumbers),
      [assignedNumbers]
   )

   const filteredGroups = useMemo(
      () => filterPhoneNumberGroups(groupedNumbers, searchQuery),
      [groupedNumbers, searchQuery]
   )

   const isLastNumber = useMemo(() => {
      return (
         filteredGroups.reduce(
            (total, group) => total + group.numbers.length,
            0
         ) === 1
      )
   }, [filteredGroups])

   useEffect(() => {
      setCount(assignedNumbers.length)
   }, [assignedNumbers.length, setCount])

   useEffect(() => {
      setSelectedTab('list')
      setSearchQuery('')
   }, [title])

   const handleDeleteClick = (number: IPhoneNumber) => {
      setNumberToDelete(number)
      setIsDeleteModalOpen(true)
   }

   const handleConfirmDelete = async () => {
      if (!numberToDelete) return
      setIsDeleting(true)
      try {
         await CampaignService.removeCampaignPhoneNumber(
            title as string,
            numberToDelete.phoneNumber.phoneNumber
         )

         mutate((currentData: any) => ({
            ...currentData,
            assignedPhoneNumbers: currentData?.assignedPhoneNumbers?.filter(
               (item: any) =>
                  item.phoneNumber !== numberToDelete.phoneNumber.phoneNumber
            ),
         }), false)

         const formatted =
            formatPhoneNumber(numberToDelete.phoneNumber.phoneNumber)
         message.success(`${formatted} removed successfully`)
         revalidateLeadSource()
         setIsDeleteModalOpen(false)
         setNumberToDelete(null)
      } catch (error) {
         console.error('Failed to remove phone number:', error)
         message.error('Failed to remove phone number')
      } finally {
         setIsDeleting(false)
      }
   }

   const handleCloseDeleteModal = () => {
      if (isDeleting) return
      setIsDeleteModalOpen(false)
      setNumberToDelete(null)
   }

   const handleConfirmPurchase = async (numbers: IAvailablePhoneNumber[]) => {
      for (const number of numbers) {
         await CampaignService.addCampaignPhoneNumber(
            title as string,
            number as any
         )
      }
      await mutate(undefined, { revalidate: true })
      revalidateLeadSource()
      message.success(
         `Successfully added ${numbers.length} phone number${
            numbers.length !== 1 ? 's' : ''
         }`
      )
   }

   const handleConfirmAllocate = async (numbers: IPhoneNumber[]) => {
      for (const number of numbers) {
         await CampaignService.addCampaignPhoneNumber(title as string, {
            phoneNumber: number.phoneNumber.phoneNumber,
            friendlyName: number.phoneNumber.friendlyName,
            locality: number.phoneNumber.locality,
            region: number.phoneNumber.region,
            lata: number.phoneNumber.lata,
            rateCenter: number.phoneNumber.rateCenter,
            latitude: number.phoneNumber.latitude || '',
            longitude: number.phoneNumber.longitude || '',
            postalCode: number.phoneNumber.postalCode || '',
            isoCountry: number.phoneNumber.isoCountry,
            addressRequirements: number.phoneNumber.addressRequirements,
            beta: number.phoneNumber.beta,
            capabilities: number.phoneNumber.capabilities,
         } as any)
      }
      await mutate(undefined, { revalidate: true })
      revalidateLeadSource()
      message.success(
         `Successfully added ${numbers.length} phone number${
            numbers.length !== 1 ? 's' : ''
         }`
      )
   }

   if (selectedTab === 'buy') {
      return (
         <div className="flex flex-col flex-1 p-4 h-full">
            <BuyNewNumbersView
               setSelectedTab={() => setSelectedTab('list')}
               onConfirmPurchase={handleConfirmPurchase}
            />
         </div>
      )
   }

   if (selectedTab === 'allocate') {
      return (
         <div className="flex flex-col flex-1 p-4 h-full">
            <AllocateUnusedNumbersView
               groupedInactivePhoneNumbers={groupedInactivePhoneNumbers}
               campaignId={title as string}
               setSelectedTab={() => setSelectedTab('list')}
               onConfirmAllocate={handleConfirmAllocate}
            />
         </div>
      )
   }

   return (
      <div className="flex flex-col flex-1 p-4 h-full">
         <div className="flex flex-col gap-y-6">
            <div className="flex flex-wrap gap-3">
               <motion.button
                  className="bg-[#9061DA]/20 rounded-lg px-6 py-3 flex items-center justify-between text-white whitespace-nowrap hover:bg-[#9061DA]/30 flex-1"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedTab('buy')}
               >
                  <span>Purchase new numbers</span>
                  <ChevronRightIcon className="text-white w-4 h-4 ml-2" />
               </motion.button>
               <motion.button
                  className="bg-[#9061DA]/20 rounded-lg px-6 py-3 flex items-center justify-between text-white whitespace-nowrap hover:bg-[#9061DA]/30 flex-1"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedTab('allocate')}
               >
                  <span>Allocate unused numbers</span>
                  <ChevronRightIcon className="text-white w-4 h-4 ml-2" />
               </motion.button>
            </div>

            <PhoneNumberSearchInput
               value={searchQuery}
               onChange={setSearchQuery}
               placeholder="Search Phone numbers"
               className="max-w-full"
            />

            {filteredGroups.length > 0 ? (
               <PhoneNumbers
                  numberGroups={filteredGroups}
                  onDelete={handleDeleteClick}
                  showMessageCount={false}
               />
            ) : (
               <div className="flex items-center justify-center py-12">
                  <p className="text-white/40 text-sm">
                     No phone numbers found
                     {searchQuery && ` for "${searchQuery}"`}
                  </p>
               </div>
            )}

            <DeletePhoneNumberModal
               isOpen={isDeleteModalOpen}
               onClose={handleCloseDeleteModal}
               onConfirm={handleConfirmDelete}
               phoneNumber={numberToDelete || undefined}
               isLastNumber={isLastNumber}
               isLoading={isDeleting}
            />
         </div>
      </div>
   )
}
